import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLangContext } from "context/Lang";
import { AsyncProcess } from "services/AsyncProcess";
import { routes } from "routes";
import { Button } from "components/Button/Button";
import { Loader } from "components/Loader/Loader";
import { motion } from "framer-motion";
import { UserPlus, ShieldCheck, ArrowLeft, Map as MapIcon, SaveIcon, Trash2Icon, BoxesIcon } from "lucide-react";
import { DialogModal } from "components/dialog-modal/dialog-modal";
import { useDispatch } from "react-redux";
import { setToast } from "storeRedux/slices/toast";
import { Message } from "primereact/message";
import { AddEditorDialogContent } from "./add-edit-dialog-content";
import { PreviewMap } from "./preview-map";
import { StatusFlags, StatusMap, StatusType } from "utils/Status";
import { StatusLabel } from "components/status-label";


export const Map = () => {
    const navigate = useNavigate();
    const { gW } = useLangContext();
    const { id } = useParams() as { id: any };
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [permission, setPermission] = useState<boolean>(true);
    const [mapItem, setMapItem] = useState<MapType | null>(null);
    
    const [editor, setEditor] = useState<MapEditorType|null>(null);
    const [selectedEditorId, setSelectedEditorId] = useState<number | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState<MapEditorType|null>(null);

    const [openEditorDialog, setOpenEditorDialog] = useState<boolean>(false);
    const [deleteEditorDialog, setDeleteEditorDialog] = useState<MapEditorType | null>(null);

    // 1. جلب تفاصيل الخريطة
    const fetchMapDetails = async () => {
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.maps}/${id}`, // تأكد من وجود هذا المسار في routes
            setError: () => { setLoading(false); setPermission(false); },
            effectProcess: [(res) => {
                if (res?.success === false) {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                    setPermission(false);
                    setLoading(false);
                } else {
                    setMapItem(res?.data);
                    setEditor(res?.data?.editors?.[0]||null);
                }
            }],
        });
    };



    
    const handleSaveEditor = () => {


        if (!selectedEditorId) return;
        setLoading(true);

        AsyncProcess({
            type: "post",
            url: routes.api.mapEditor,
            data: {
                "editorId": selectedEditorId,
                "mapId": mapItem?.id
            },
            setError: () => {
                setLoading(false);
                dispatch(setToast({ detail: gW("happen-error"), severity: "error" }));
            },
            effectProcess: [(res) => {
                if (res?.success !== false) {
                    setEditor(res?.data);
                    setOpenEditorDialog(false);
                    dispatch(setToast({ detail: gW("editor-assigned-success"), severity: "success" }));
                } else {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                }
                setLoading(false);
            }]
        });
    };

    // 4. حذف محرر
    const confirmRemoveEditor = () => {
        if (!deleteEditorDialog?.id) return;
        setLoading(true);
        const editorId = deleteEditorDialog.id;

        AsyncProcess({
            type: "post",
            url: `${routes.api.mapEditor}/${editorId}`,
            setError: () => {
                dispatch(setToast({ detail: gW("happen-error"), severity: "error" }));
                setLoading(false);
                setDeleteEditorDialog(null);
            },
            effectProcess: [(res) => {
                if (res?.success !== false) {
                    // setEditor(prev => prev.filter(e => e.id !== editorId));
                    setEditor(null);
                    dispatch(setToast({ detail: gW("editor-removed"), severity: "success" }));
                }
                setLoading(false);
                setDeleteEditorDialog(null);
            }]
        });
    };


    useEffect(() => {
        if (id) fetchMapDetails();
    }, [id]);

    // مكون بطاقة المحرر (نفس تصميم Building)
    const editorCard = ({ editor, isActive }: { editor: MapEditorType, isActive?: boolean }) => (
        <div key={editor.id} className="flex items-center justify-between p-3 bg-primary-50/20 rounded-xl border border-primary-100">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                    {editor.editor?.name?.[0]}{editor.editor?.surname?.[0]}
                </div>
                <div>
                    <p className="text-sm font-medium text-primary-800">{editor.editor?.name} {editor.editor?.surname}</p>
                    <p className="text-xs text-primary-500">{editor.editor?.username}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-bold">
                    <ShieldCheck size={14} /> {gW("authorized")}
                </div>
                <Button 
                    variant="outline" 
                    className="border-red-500 text-red-500 hover:bg-red-50 p-2 h-auto"
                    onClick={() => setDeleteEditorDialog(editor)}
                >
                    <Trash2Icon size={16} />
                </Button>
            </div>
        </div>
    );

    if (loading && !mapItem) return <div className="flex justify-center p-20"><Loader /></div>;
    if (!permission) return <Message className="w-full" severity="warn" text={gW("no-permission")} />;

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mx-auto">
            {/* Header */}
            <div className="flex   items-center gap-4 mb-8">
                <div className="flex  items-center gap-4 " >

                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-primary-800">{mapItem?.title}</h1>
                        <p className="text-primary-500 flex items-center gap-1 italic">
                            <MapIcon size={16} /> {mapItem?.description || gW("map-editor-management")}
                        </p>
                    </div>
                </div>

                <div>
                    <StatusLabel status={ mapItem?.status }   />
                    {/* <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        mapItem?.status === StatusFlags.ACTIVE ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}>
                        {gW(StatusMap?.[mapItem?.status as StatusType])}
                    </span> */}
                    {/* {gW(StatusMap?.[mapItem?.status as StatusType ])} */}
                </div>


            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="md:col-span-2 bg-white rounded-2xl border border-primary-100 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">{gW("map-details")}</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between py-2 border-b border-primary-50">
                            <span className="text-primary-500">{gW("map-id")}</span>
                            <span className="font-medium">#{mapItem?.id}</span>
                        </div>
                    </div>

                    <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <ShieldCheck size={18} className="text-primary-600" />
                            {gW("manage-editor")}
                        </h3>
                        <div className="space-y-3">
                            {editor ===null ? (
                                <p className="text-primary-400 italic text-sm">{gW("no-editor-assigned")}</p>
                            ) : (editorCard({ editor, isActive: true }))}
                        </div>
                    </div>
                </div>

                {/* Sidebar Actions */}
                <div className="flex flex-col gap-4">
                    <div className="bg-primary-50/50 p-6 rounded-2xl border border-primary-100">
                        <h3 className="text-primary-800 font-bold mb-4 flex items-center gap-2">
                            <ShieldCheck size={18} /> {gW("actions")}
                        </h3>
                        <div className="flex flex-col gap-3">
                            <Button
                                variant="outline"
                                className="w-full justify-start gap-2 border-primary-200 text-primary-700 hover:bg-primary-50"
                                onClick={() => setOpenEditorDialog(true)}
                            >
                                <UserPlus size={18} />
                                {editor!==null ? gW("change-editor") : gW("assign-editor")}
                            </Button>

                            <Button
                                disabled={!editor?.id}
                                variant="ghost"
                                className="w-full justify-start gap-2 text-primary-600 hover:text-primary-600"
                                onClick={() => setIsPreviewOpen(editor!)}
                            >
                                <MapIcon size={18} />
                                {gW(!editor?.id?"no-active-map":"view-active-map")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Change Editor Dialog */}
            <DialogModal
                size="3xl"
                isOpen={openEditorDialog}
                onClose={() => setOpenEditorDialog(false)}
                title={gW("assign-map-editor")}
                actions={[
                    <Button variant="outline" onClick={handleSaveEditor} disabled={!selectedEditorId}>
                        <SaveIcon className="mr-2" size={18} />
                        {gW("save")}
                    </Button>
                ]}
            >
                {openEditorDialog && (
                    <AddEditorDialogContent
                        initEditorIds={[editor?.editor?.id] as number[]}
                        onChangeEditorId={(e) => {
                            setSelectedEditorId(e);
                        }}
                    />
                )}
            </DialogModal>

            <DialogModal
                size="md"
                isOpen={!!deleteEditorDialog}
                onClose={() => setDeleteEditorDialog(null)}
                title={gW("confirm-delete")}
                actions={[
                    <Button variant="ghost" onClick={() => setDeleteEditorDialog(null)}>{gW("cancel")}</Button>,
                    <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmRemoveEditor}>
                        <Trash2Icon className="mr-2" size={16} /> {gW("delete")}
                    </Button>
                ]}
            >
                <div className="p-4 text-center">
                    <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Trash2Icon className="text-red-600" size={32} />
                    </div>
                    <p className="text-primary-600">{gW("are-you-sure-delete-editor")}</p>
                    <p className="font-bold text-primary-800 mt-2">
                        {deleteEditorDialog?.editor?.name} {deleteEditorDialog?.editor?.surname}
                    </p>
                </div>
            </DialogModal>

            <DialogModal
                size="2xl"
                isOpen={isPreviewOpen!==null}
                onClose={() => setIsPreviewOpen(null)}
                title={`${gW("preview")}: ${mapItem?.title}`}
            >
                
                {isPreviewOpen && <PreviewMap data={isPreviewOpen} />}
            </DialogModal>
        </motion.div>
    );
};